import type { ReactNode } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../theme";
import { expandedAria } from "../a11y";

// THE text-action for card/row action strips — the house idiom
// (Vedi ▾ · Segui · Modifica › · Elimina) that the Programs/Plans rows
// hand-rolled per-file via programsPlanStyles (rowAction + changeLinkText
// + deleteLinkText). One place for the 44pt touch floor, the pressed dim,
// the ink3-when-disabled recolor and the destructive
// variant, so the idiom can't drift again (§CONSOLIDAMENTO, HR-S4).
// State is never colour-alone: disabled also lands in a11y state and,
// by house convention, in the label itself (e.g. "Following…").
export function RowAction({
  theme,
  label,
  onPress,
  disabled = false,
  destructive = false,
  emphasis = false,
  accessibilityLabel,
  accessibilityHint,
  expanded,
  trailing,
}: {
  theme: UiTheme;
  label: string;
  onPress: () => void;
  disabled?: boolean;
  /** Danger-coloured label (delete). */
  destructive?: boolean;
  /** The row's primary action: heavier and in the brand ink, never colour alone. */
  emphasis?: boolean;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  /** Set on expand/collapse toggles (lands in a11y state). */
  expanded?: boolean;
  /** Trailing adornment (e.g. the ▾ chevron of a preview toggle). */
  trailing?: ReactNode;
}) {
  const styles = makeStyles(theme);
  const text = (
    <Text
      style={[
        destructive ? styles.destructiveText : emphasis ? styles.emphasisText : styles.text,
        disabled && !destructive && styles.disabledText,
      ]}
    >
      {label}
    </Text>
  );
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{
        disabled: disabled || undefined,
        expanded,
      }}
      {...expandedAria(expanded)}
      disabled={disabled}
      onPress={onPress}
      hitSlop={8}
      style={({ pressed }) => [
        styles.action,
        // #472: ink3 is the disabled dim; at 0.6 it fell under 4.5:1.
        pressed && !disabled && styles.pressed,
      ]}
    >
      {trailing ? (
        <View style={styles.iconRow}>
          {text}
          {trailing}
        </View>
      ) : (
        text
      )}
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    // 44pt floor both ways: a short word ("Open") is narrower than that,
    // and the target has to be 44 wide too (the strip is dense, hitSlop
    // tops it up).
    action: {
      minHeight: 44,
      minWidth: 44,
      paddingVertical: s.s2,
      justifyContent: "center",
      alignItems: "center",
    },
    pressed: { opacity: 0.6 },
    text: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    // brandText is gated at 4.5:1 on every surface, both themes.
    emphasisText: {
      color: p.brandText,
      fontSize: t.sizes.sm,
      fontWeight: "700",
    },
    disabledText: {
      color: p.ink3,
    },
    destructiveText: {
      color: p.danger,
      fontSize: t.sizes.sm,
      fontWeight: "700",
    },
    iconRow: { flexDirection: "row", alignItems: "center", gap: s.s2 },
  });
}
